import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AanmeldShell } from "@/components/AanmeldShell";
import { Card, CardContent } from "@/components/ui/card";
import { SkeletonLine } from "@/components/ui/skeleton";
import { BeschikbaarheidForm } from "@/features/beschikbaarheid/BeschikbaarheidForm";
import { GeenBevoegdheidKaart } from "@/features/beschikbaarheid/GeenBevoegdheidKaart";
import { useOndersteunerPeriodes } from "@/features/beschikbaarheid/useBeschikbaarheid";
import { InschrijfperiodeKiezer } from "@/features/inschrijving/InschrijfperiodeKiezer";
import { huidigePeriode, vensterStatus } from "@/features/inschrijving/venster";
import { useSeizoenen } from "@/features/session/useSeizoenen";

/**
 * §4.3 on the persoon axis, off the korps axis entirely: a jeugdleider who is nobody's
 * korpscontact is still an ondersteuner. Only the bevoegdheid decides whether this screen exists.
 */
export function BeschikbaarheidPage() {
  const { t } = useTranslation(["beschikbaarheid", "inschrijving"]);
  const { data: seizoenen } = useSeizoenen();
  const seizoen = seizoenen?.[0];
  const { data: periodes, isPending: periodesPending } = useOndersteunerPeriodes(seizoen?.id);
  const [gekozenPeriodeId, setGekozenPeriodeId] = useState<string>();

  // The open one to start with, otherwise the first there is. A shut one still draws its list:
  // "wat had ik ook alweer opgegeven" is asked precisely after the deadline (ADR-0031).
  const periode =
    periodes?.find((kandidaat) => kandidaat.id === gekozenPeriodeId) ?? huidigePeriode(periodes);
  const venster = vensterStatus(periode);

  return (
    <AanmeldShell
      titel={t("beschikbaarheid:title")}
      uitleg={
        // The window's own status, open or shut - the deadline is what someone reads here.
        // What the screen is *for* is said above the checkboxes instead, where it survives the
        // periode closing and where there is something for it to describe.
        //
        // A bar until the periodes land: "er staat nog geen inschrijfperiode open" is what an
        // empty answer means, not what a pending one does.
        !seizoen ? (
          t("beschikbaarheid:geenSeizoen")
        ) : periodesPending ? (
          <SkeletonLine className="w-72" />
        ) : periode ? (
          t("inschrijving:venster.metPeriode", {
            periode: periode.name,
            venster: venster.label,
          })
        ) : (
          venster.label
        )
      }
    >
      {(session) =>
        !session.hasBevoegdheid ? (
          <GeenBevoegdheidKaart />
        ) : (
          <Card>
            <CardContent className="space-y-6 pt-6">
              {/* Only where there is something to choose: a seizoen usually gives ondersteuners
                one window, and a picker offering one option is a control to read past. */}
              {periodes && periodes.length > 1 && (
                <InschrijfperiodeKiezer
                  id="beschikbaarheid-periode"
                  periodes={periodes}
                  gekozen={periode?.id}
                  onKies={setGekozenPeriodeId}
                />
              )}

              <BeschikbaarheidForm
                inschrijfperiodeId={periode?.id}
                persoonId={session.persoonId ?? undefined}
                gesloten={!venster.open}
              />
            </CardContent>
          </Card>
        )
      }
    </AanmeldShell>
  );
}
